/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { UserService, UserStorageService } from '@vsolv/core/users/web';
import { FIREBASE_AUTH } from '../tokens';
import { authState$, idToken$ } from '../utils/firebase-auth.rx';
import {
  Auth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  updatePassword,
} from 'firebase/auth';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Injectable()
export class FirebaseService implements OnDestroy {
  public refresh$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  // Initialize authState inside the DI context (constructor),
  // rather than on-demand via a getter, to avoid Firebase observer setup
  // "outside of injection context" warnings.
  public readonly authUser$ = authState$(this.auth);

  private _onDestroy$ = new Subject<void>();

  constructor(
    @Inject(FIREBASE_AUTH) private auth: Auth,
    private http: HttpClient,
    private userStorageSvc: UserStorageService,
    private userSvc: UserService
  ) {
    this.authUser$.pipe(takeUntil(this._onDestroy$)).subscribe(async _user => {
      // if (user) await firstValueFrom(this.http.put(`/api/users/${user.uid}`, {}));
    });
  }

  ngOnDestroy() {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  getAuthId$(): Observable<string | undefined> {
    return this.authUser$.pipe(map(authUser => authUser?.uid));
  }

  isSignedIn$() {
    return this.authUser$.pipe(map(state => !!state));
  }

  getAuthToken$() {
    return idToken$(this.auth);
  }

  async emailAndPasswordSignIn(email: string, password: string) {
    const tenantId = (await this.getCurrentFirebaseTenant()).firebaseTenantId;
    if (!tenantId) throw new Error('Something went wrong establishing a connection to the identity server');

    const auth = this.getFirebaseTenantAuth(tenantId);

    const user = await signInWithEmailAndPassword(auth, email, password);
    await this.userSvc.updateLastSignedIn();

    return user;
  }

  async sendProtectedLoginEmail(email: string, redirect?: string) {
    // Keep the email in storage so we can prefill subsequent flows
    this.userStorageSvc.saveEmail(email);
    return await firstValueFrom(this.http.post(`/v1/auth/send-protected-login-email`, { email, redirect }));
  }

  async forgotPassword(email: string) {
    return await firstValueFrom(this.http.post<void>(`/v1/auth/send-forgot-password-email`, { email }));
  }

  async updatePassword(password: string) {
    const tenantId = (await this.getCurrentFirebaseTenant()).firebaseTenantId;
    if (!tenantId) throw new Error('Something went wrong establishing a connection to the identity server');

    const auth = this.getFirebaseTenantAuth(tenantId);

    const user = auth.currentUser;
    if (!user) throw new Error('Something went wrong establishing a connection to the identity server');

    await updatePassword(user, password);
  }

  async passwordlessSignIn(tenantId: string, email: string) {
    if (!email) return;
    this.userStorageSvc.clearEmail();

    const auth = this.getFirebaseTenantAuth(tenantId);

    return await signInWithEmailLink(auth, email);
  }

  async customTokenSignIn(token: string, tenantId: string) {
    const auth = this.getFirebaseTenantAuth(tenantId);

    const user = await signInWithCustomToken(auth, token);
    await this.userSvc.updateLastSignedIn();
    return user;
  }

  async getCurrentFirebaseTenant() {
    return await firstValueFrom(this.http.get<{ firebaseTenantId?: string }>(`/v1/auth/firebase`));
  }

  async signOut() {
    await signOut(this.auth);
    this.userStorageSvc.clearSignedInUser();
    sessionStorage.clear();
  }

  async validateCurrentUser() {
    const user = await firstValueFrom(this.authUser$);
    if (user) {
      const firebase = await this.getCurrentFirebaseTenant();
      if (user.tenantId !== firebase.firebaseTenantId) {
        await this.signOut();
        return false;
      }
    }
    return true;
  }

  async exchangeToken(email: string, token: string) {
    return await firstValueFrom(
      this.http.put<{ signInToken: string; tenantId: string } | undefined>(`/v1/auth/exchangeVsolvToken`, {
        email,
        token,
      })
    );
  }

  async exchangeLoginToken(email: string, token: string) {
    return await firstValueFrom(
      this.http.put<{ firebaseMagicLink: string }>(`/v1/auth/exchange-login-token`, {
        email,
        token,
      })
    );
  }

  async reauthenticate(password: string) {
    try {
      const currentUser = this.auth.currentUser;

      if (!currentUser || !currentUser.email) return false;

      //switch the auth to the tenant id of the current user
      this.getFirebaseTenantAuth(currentUser.tenantId);

      const credential = EmailAuthProvider.credential(currentUser.email, password);
      const result = await reauthenticateWithCredential(currentUser, credential);

      if (result) return true;
    } catch (err) {
      console.error(err);
    }
    return false;
  }

  private getFirebaseTenantAuth(tenantId: string | null) {
    this.auth.tenantId = tenantId;
    return this.auth;
  }
}
